'use client';

import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { AuthenticationStatus } from '@/enums/authentication-status';
import { useProfile } from '@/hooks/profile/profile';
import { HeaderLink } from '@/types/header-link';
import { dataTestTransformer } from '@/utils/data-test-helper';
import clsx from 'clsx';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import React from 'react';
import { secondaryLinks } from '../secondary-header/secondary-nav/secondary-nav-links';
import mainHeaderStyles from './main-header.module.scss';
import { MobileNavMenu } from './mobile-nav-menu/nav-menu';
import { SignInCreateAccount } from './sign-in-create-account';
import { UserProfile } from './user-profile/user-profile';

type MainHeaderProps = {
  hideNavLinks?: boolean;
};

export const MainHeader = ({ hideNavLinks = false }: MainHeaderProps): React.JSX.Element => {
  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const { status } = useSession();
  const primaryLinks: HeaderLink[] = [
    { href: '/movies', title: 'Movies', useAnchor: true },
    { href: '/theatres', title: 'Theatres', useAnchor: true },
    { href: '/movie-rewards', title: 'Movie Rewards', useAnchor: true },
  ];
  const modifiedSecondaryLinks = structuredClone(secondaryLinks);

  if (profile) {
    modifiedSecondaryLinks.push({ href: '/account/profile', title: 'Your Account' });
  }

  const displaySignInOrUserProfile = (): React.JSX.Element => {
    if (profile || isLoadingProfile || status === AuthenticationStatus.LOADING) return <UserProfile />;

    return <SignInCreateAccount />;
  };

  return (
    <nav className="flex items-center justify-center max-md:h-[60px] shadow-lg">
      <div className={clsx('flex flex-auto items-center justify-between container-xl', mainHeaderStyles['main-header'])}>
        <div className="flex items-center gap-ms lg:gap-md">
          {!hideNavLinks &&
            <MobileNavMenu className="lg:hidden"
              primaryLinks={primaryLinks}
              secondaryLinks={modifiedSecondaryLinks}
            />
          }
          <CinemarkLink useAnchor
            dataLinkCategory={AnalyticsPageCategories.MAIN_NAV}
            dataLinkName="Home"
            dataTest="logo-link"
            href="/"
          >
            <Image alt="Cinemark logo"
              className="max-w-[30px] lg:hidden"
              height={60}
              src="/img/cinemarkC.png"
              width={60}
            />
            <Image alt="Cinemark logo"
              className="max-w-[200px] hidden lg:inline-block"
              height={42}
              src="/img/cinemarkLogo.png"
              width={301}
            />
          </CinemarkLink>
          {!hideNavLinks &&
            <ul className="hidden lg:flex items-center gap-md"
              data-test="primary-links"
            >
              {primaryLinks.map((item, index) =>
                <li key={index}
                  className="list-item text-sm"
                >
                  <CinemarkLink className="text-primary text-lg font-semibold"
                    dataLinkCategory={AnalyticsPageCategories.MAIN_NAV}
                    dataLinkName={item.title}
                    dataTest={`primary-link-${dataTestTransformer(item.title)}`}
                    href={item.href}
                    useAnchor={item.useAnchor}
                  >
                    {item.title}
                  </CinemarkLink>
                </li>
              )}
            </ul>
          }
        </div>
        { displaySignInOrUserProfile() }
      </div>
    </nav>
  );
};
